import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Utensils, ChevronDown, ChevronUp, Image as ImageIcon, Camera, X, LayoutGrid, ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import MobileView from '../../Experience/components/MobileView';
import AddItemDialog from './AddItemDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useMenuStore } from '../../../stores/Assets/menu';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { useSiteStore } from '../../../stores/Assets/site';
import { useUsageStore } from '../../../stores/Usage';
import { useKitchenStore } from '../../../stores/Assets/kitchen';
import { Button, Modal } from 'reactstrap';
import { PlanUpgradeModal } from '../../../Components/Common/PlanUpgradeModal';

const THEME_COLOR = '#c59253';

// --- Transformer Helpers ---
const transformSiteDataToState = (menus) => {
    if (!menus || !Array.isArray(menus)) return { sections: [] };

    return {
        sections: menus.map(menu => ({
            id: menu.menu_id,
            name: menu.name,
            name_ar: menu.name_ar,
            description: menu.description,
            description_ar: menu.description_ar,
            imagePreview: menu.image_url,
            // Items
            items: (menu.menu_items || []).map(item => ({
                id: item.menu_item_id,
                name: item.title,
                name_ar: item.title_ar,
                description: item.description,
                description_ar: item.description_ar,
                price: item.price,
                promo_price: item.promo_price || '',
                is_promo: item.is_promo ?? false,
                isHidden: item.isHidden ?? false,
                isListed: item.isListed ?? true,
                available: item.available ?? true,
                imagePreview: item.image_url || (item.item_images?.[0]?.image_url),
                tags: item.tags || [],
                options: (item.menuItem_options || []).map(optConn => {
                    const opt = optConn.menu_item_option;
                    return {
                        name: opt.name,
                        choices: opt.choices || []
                    };
                })
            }))
        }))
    };
};

const transformStateToSitePayload = (state, deletedMenuIds, deletedItemIds) => {
    return {
        menus: (state.sections || []).map(section => ({
            menu_id: section.id,
            name: section.name,
            name_ar: section.name_ar,
            description: section.description,
            description_ar: section.description_ar,
            image_url: section.imagePreview,
            currency: state.currency,
            // Items
            items: (section.items || []).map(item => ({
                menu_item_id: item.id,
                title: item.name,
                title_ar: item.name_ar,
                description: item.description,
                description_ar: item.description_ar,
                price: item.price,
                promo_price: item.promo_price,
                is_promo: item.is_promo,
                isHidden: item.isHidden,
                isListed: item.isListed,
                available: item.available,
                image_url: item.imagePreview,
                tags: item.tags || [],
                options: item.options || [],
            }))
        })),
        deleted_menu_ids: deletedMenuIds,
        deleted_item_ids: deletedItemIds
    };
};
const CURRENCIES = [

    { code: 'QAR', symbol: 'QR', name: 'Qatari Riyal' },
    { code: 'DZD', symbol: 'DZD', name: 'Algerian Dinar' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'SAR', symbol: '⃁', name: 'Saudi Riyal' },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },

];


const EditBranchMenu = ({ siteId, onClose }) => {
    //will have another seciton at the top to edit the infos of the branch make it a dropdown as well 
    const { getSiteMenuFull, saveSiteMenuStructure, isLoading: storeLoading } = useMenuStore();
    const { getLimitInfo } = useUsageStore();
    const { updateSite, createSite } = useSiteStore();
    const { createKitchen } = useKitchenStore();
    const [hasChanges, setHasChanges] = useState(false);
    const [localState, setLocalState] = useState({ sections: [] });
    const [siteInfo, setSiteInfo] = useState(null);
    const [isBranchInfoExpanded, setIsBranchInfoExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const [deletedMenuIds, setDeletedMenuIds] = useState([]);
    const [deletedItemIds, setDeletedItemIds] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [currency, setCurrency] = useState('QAR');
    const [isSaving, setIsSaving] = useState(false);
    const [saveLogs, setSaveLogs] = useState([]);
    const [saveStats, setSaveStats] = useState(null);

    const [isLoadingLocal, setIsLoadingLocal] = useState(true);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const { fetchUsage } = useUsageStore();
    const menuLimit = getLimitInfo("menus");
    const itemLimit = getLimitInfo("menuItems");

    // --- Fetch Data ---
    useEffect(() => {
        const fetchMenu = async () => {
            setIsLoadingLocal(true);
            fetchUsage(); // Fetch usage on mount to have latest info
            if (siteId && siteId !== 'new') {
                const response = await getSiteMenuFull(siteId);
                if (response) {
                    const { site, menus } = response;
                    setSiteInfo(site);
                    const transformed = transformSiteDataToState(menus);
                    setLocalState(transformed);
                    if (transformed.sections.length > 0) {
                        setActiveSection(transformed.sections[0].id);
                    }
                }
            } else if (siteId === 'new') {
                // Initialize empty state for new branch
                setSiteInfo({
                    name: '',
                    name_ar: '',
                    phone: '',
                    address: '',
                    address_ar: '',
                    image_url: null
                });
                setLocalState({ sections: [] });
                setIsBranchInfoExpanded(true); // Open info section by default for new branch
            }
            setIsLoadingLocal(false);
        };
        fetchMenu();
    }, [siteId]);

    const updateState = (newState) => {
        setLocalState(prev => ({ ...prev, ...newState }));
    };

    // --- Save Logic ---
    const handleSave = async () => {
        const currentSections = localState.sections || [];
        const totalItemsCount = currentSections.reduce(
            (acc, section) => acc + (section.items?.length || 0),
            0
        );

        setSaveLogs([]);
        setSaveStats({
            totalSections: currentSections.length,
            totalItems: totalItemsCount,
            deletedMenus: deletedMenuIds.length,
            deletedItems: deletedItemIds.length,
            isNewBranch: siteId === 'new',
        });

        const addLog = (message) => {
            setSaveLogs((prev) => [...prev, message]);
        };

        setIsSaving(true);
        try {
            let activeSiteId = siteId;

            // 1. Create Site if it's a new branch
            if (siteId === 'new') {
                if (!siteInfo?.name?.trim()) {
                    toast.error("Please enter a branch name");
                    setIsSaving(false);
                    return;
                }

                addLog('Creating new branch...');
                const sitePayload = new FormData();
                sitePayload.append('name', siteInfo.name);
                sitePayload.append('name_ar', siteInfo.name_ar || '');
                sitePayload.append('phone', siteInfo.phone || '');
                sitePayload.append('address', siteInfo.address || '');
                sitePayload.append('address_ar', siteInfo.address_ar || '');
                if (siteInfo.imageFile) {
                    sitePayload.append('file', siteInfo.imageFile);
                }

                const newSite = await createSite(sitePayload);
                if (!newSite) {
                    toast.error("Failed to create branch");
                    setIsSaving(false);
                    return;
                }
                addLog(`Branch created (ID ${newSite.site_id})`);

                // Create Kitchen under the hood
                addLog('Creating kitchen for branch...');
                await createKitchen({
                    site_id: newSite.site_id,
                    name: siteInfo.name,
                    is_open: true
                });
                addLog('Kitchen created.');

                activeSiteId = newSite.site_id;
            } else if (siteInfo) {
                // Update existing site info
                addLog('Updating branch information...');
                const sitePayload = new FormData();
                sitePayload.append('name', siteInfo.name);
                sitePayload.append('name_ar', siteInfo.name_ar || '');
                sitePayload.append('phone', siteInfo.phone || '');
                sitePayload.append('address', siteInfo.address || '');
                sitePayload.append('address_ar', siteInfo.address_ar || '');
                if (siteInfo.imageFile) {
                    sitePayload.append('file', siteInfo.imageFile);
                }

                await updateSite(siteId, sitePayload);
                addLog('Branch information updated.');
            }

            // 2. Save Menu Structure
            addLog('Saving menu structure...');
            const payload = transformStateToSitePayload(localState, deletedMenuIds, deletedItemIds);
            const success = await saveSiteMenuStructure(activeSiteId, payload);

            if (success) {
                addLog('Menu structure saved successfully.');
                toast.success(siteId === 'new' ? 'Branch created successfully' : 'Branch details and menu updated successfully');
                onClose();
            } else {
                addLog('Failed to update menu structure.');
                toast.error('Failed to update menu structure');
            }
        } catch (error) {
            console.error(error);
            setSaveLogs((prev) => [...prev, `Error: ${error?.message || 'Unknown error'}`]);
            toast.error('An error occurred while saving');
        } finally {
            setIsSaving(false);
        }
    };

    // --- State Accessors ---
    const sections = localState.sections || [];
    const totalItems = sections.reduce((acc, section) => acc + (section.items?.length || 0), 0);

    // --- Action Handlers ---
    const addSection = () => {
        if (menuLimit?.isReached) {
            setShowUpgradeModal(true);
            return;
        }
        setHasChanges(true);
        const newSection = {
            id: `new_${Date.now()}`,
            name: `Menu ${(sections.length || 0) + 1}`,
            items: []
        };
        updateState({ sections: [...sections, newSection] });
        setActiveSection(newSection.id);
    };

    const deleteSection = (id, e) => {
        e.stopPropagation();
        setHasChanges(true);
        if (!window.confirm("Are you sure you want to remove this section?")) return;

        if (id && !id.toString().startsWith('new_')) {
            setDeletedMenuIds(prev => [...prev, id]);
        }

        updateState({
            sections: sections.filter((s) => s.id !== id),
        });
        if (activeSection === id) {
            setActiveSection(null);
        }
    };

    const updateSection = (id, data) => {
        updateState({
            sections: sections.map((s) => (s.id === id ? { ...s, ...data } : s)),
        });
    };

    const addItem = (item) => {
        setHasChanges(true);
        if (!activeSection) return;

        if (editingItem) {
            // Update existing item
            const updatedSections = sections.map(section => {
                if (section.id === activeSection) {
                    return {
                        ...section,
                        items: (section.items || []).map(i => i.id === editingItem.id ? { ...item, id: i.id } : i)
                    };
                }
                return section;
            });
            updateState({ sections: updatedSections });
            setEditingItem(null);
            return;
        }

        const newItem = {
            ...item,
            id: `new_${Date.now()}_${Math.random()}`,
        };

        const updatedSections = sections.map(section => {
            if (section.id === activeSection) {
                return {
                    ...section,
                    items: [...(section.items || []), newItem]
                };
            }
            return section;
        });

        updateState({ sections: updatedSections });
    };

    const deleteItem = (id) => {
        setHasChanges(true);
        if (id && !id.toString().startsWith('new_') && !id.toString().includes('.')) {
            setDeletedItemIds(prev => [...prev, id]);
        }

        const updatedSections = sections.map(section => {
            if (section.id === activeSection) {
                return {
                    ...section,
                    items: (section.items || []).filter(item => item.id !== id)
                };
            }
            return section;
        });
        updateState({ sections: updatedSections });
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setIsDialogOpen(true);
    };

    const handleSectionImage = (sectionId, e) => {
        setHasChanges(true);
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateSection(sectionId, {
                    imagePreview: reader.result,
                    imageFile: file
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSiteImage = (e) => {
        setHasChanges(true);
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSiteInfo(prev => ({
                    ...prev,
                    imagePreview: reader.result,
                    imageFile: file
                }));
            };
            reader.readAsDataURL(file);
        }
    };
    const attemptClose = () => {
        if (hasChanges) {
            setShowExitConfirm(true);
        } else {
            onClose();
        }
    };
    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-light d-flex flex-column" style={{ zIndex: 1040 }}>
            {/* --- Header --- */}
            <div className="bg-white px-4 py-3 border-bottom d-flex align-items-center justify-content-between shadow-sm">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={attemptClose} className="btn btn-light rounded-circle p-2 hover-bg-silver text-muted border">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h5 className="mb-0 fw-bold">{siteId === 'new' ? 'Create New Branch' : 'Edit Branch Menu'}</h5>
                        <p className="text-muted small mb-0">{siteId === 'new' ? 'Setup your new location and menu' : 'Manage sections and items for this branch'}</p>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isSaving || storeLoading || !hasChanges}
                    className="btn btn-primary d-flex align-items-center gap-2 px-4 rounded-pill shadow-sm"
                    style={{ backgroundColor: THEME_COLOR, borderColor: THEME_COLOR }}
                >
                    <Save size={18} />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {/* --- Main Content --- */}
            <div className="flex-grow-1 d-flex flex-column flex-lg-row overflow-hidden bg-light bg-opacity-50 position-relative">
                {isSaving && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            zIndex: 20,
                            backdropFilter: 'blur(6px)',
                            backgroundColor: 'rgba(255,255,255,0.75)',
                        }}
                    >
                        <div className="bg-white rounded-4 shadow-lg p-4 p-md-5" style={{ minWidth: '260px', maxWidth: '420px' }}>
                            <div className="d-flex flex-column align-items-center text-center mb-3">
                                <div className="spinner-border" role="status" style={{ color: THEME_COLOR }}>
                                    <span className="visually-hidden">Saving...</span>
                                </div>
                                <h6 className="mt-3 mb-1 fw-bold text-dark">Saving Changes</h6>
                                {saveStats && (
                                    <p className="text-muted small mb-0">
                                        {saveStats.totalSections} sections • {saveStats.totalItems} items
                                        {(saveStats.deletedMenus > 0 || saveStats.deletedItems > 0) && (
                                            <>
                                                {' '}
                                                • {saveStats.deletedMenus} sections removed • {saveStats.deletedItems} items removed
                                            </>
                                        )}
                                    </p>
                                )}
                            </div>
                            <div className="bg-light rounded-3 p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {saveLogs.length === 0 ? (
                                    <p className="small text-muted mb-0">Preparing save...</p>
                                ) : (
                                    <ul className="list-unstyled mb-0 small text-start">
                                        {saveLogs.map((log, idx) => (
                                            <li key={idx} className="mb-1">
                                                <span className="text-muted me-1">•</span>
                                                <span>{log}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Left: Editor Area */}
                <div className="flex-grow-1 overflow-auto custom-scroll p-3 p-lg-4">
                    {isLoadingLocal ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                            <div className="spinner-border text-[#C59253]" role="status" style={{ color: THEME_COLOR }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-4xl mx-auto" style={{ maxWidth: '900px', margin: '0 auto' }}>

                            {/* 1. Branch Information Section - Redesigned Collapsible */}
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                                <div
                                    className="p-3 px-4 bg-white d-flex align-items-center justify-content-between cursor-pointer transition-all hover-bg-silver"
                                    onClick={() => setIsBranchInfoExpanded(!isBranchInfoExpanded)}
                                >
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="p-2 rounded-3 bg-primary bg-opacity-10 text-primary">
                                            <Camera size={20} />
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold text-dark">{t("Branch Information")}</h6>
                                            <p className="text-muted mb-0" style={{ fontSize: '11px' }}>{t("Edit basic details, location and contacts")}</p>
                                        </div>
                                    </div>
                                    {isBranchInfoExpanded ? <ChevronUp size={20} className="text-muted" /> : <ChevronDown size={20} className="text-muted" />}
                                </div>

                                <AnimatePresence>
                                    {isBranchInfoExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="p-4 bg-white border-top">
                                                <div className="row g-4">
                                                    <div className="col-12 col-md-4 text-center border-end">
                                                        <div className="position-relative d-inline-block">
                                                            <div className="rounded-circle border-4 border-white shadow overflow-hidden bg-light d-flex align-items-center justify-content-center" style={{ width: '120px', height: '120px' }}>
                                                                {siteInfo?.imagePreview || siteInfo?.image_url ? (
                                                                    <img src={siteInfo.imagePreview || siteInfo.image_url} alt="Logo" className="w-100 h-100 object-fit-cover" />
                                                                ) : (
                                                                    <ImageIcon size={48} className="text-muted opacity-20" />
                                                                )}
                                                            </div>
                                                            <label className="btn btn-sm btn-dark rounded-circle p-2 position-absolute bottom-0 end-0 shadow cursor-pointer border-0">
                                                                <Camera size={14} />
                                                                <input type="file" hidden accept="image/*" onChange={handleSiteImage} />
                                                            </label>
                                                        </div>
                                                        <p className="small text-muted mt-3 mb-0">Branch Logo / Thumbnail</p>
                                                    </div>

                                                    <div className="col-12 col-md-8">
                                                        <div className="row g-3">
                                                            <div className="col-12 col-sm-6">
                                                                <label className="small fw-bold text-muted mb-1 text-uppercase" style={{ fontSize: '10px' }}>{t("Branch Name (EN)")}</label>
                                                                <input
                                                                    className="form-control rounded-3 border-light border-2 shadow-none ps-3"
                                                                    value={siteInfo?.name || ''}
                                                                    onChange={(e) => { setHasChanges(true); setSiteInfo({ ...siteInfo, name: e.target.value }) }}
                                                                />
                                                            </div>
                                                            <div className="col-12 col-sm-6">
                                                                <label className="small fw-bold text-muted mb-1 d-block text-end text-uppercase" style={{ fontSize: '10px' }}>{t("Branch Name (AR)")}</label>
                                                                <input
                                                                    dir="rtl"
                                                                    className="form-control rounded-3 border-light border-2 shadow-none text-end pe-3"
                                                                    value={siteInfo?.name_ar || ''}
                                                                    onChange={(e) => { setHasChanges(true); setSiteInfo({ ...siteInfo, name_ar: e.target.value }) }}
                                                                />
                                                            </div>

                                                            <div className="col-12 col-sm-6">
                                                                <label className="small fw-bold text-muted mb-1 text-uppercase text-start d-block" style={{ fontSize: '10px' }}>{t("Address (EN)")}</label>
                                                                <input
                                                                    className="form-control rounded-3 border-light border-2 shadow-none ps-3"
                                                                    value={siteInfo?.address || ''}
                                                                    onChange={(e) => { setHasChanges(true); setSiteInfo({ ...siteInfo, address: e.target.value }) }}
                                                                />
                                                            </div>
                                                            <div className="col-12 col-sm-6">
                                                                <label className="small fw-bold text-muted mb-1 text-uppercase text-end d-block" style={{ fontSize: '10px' }}>{t("Address (AR)")}</label>
                                                                <input
                                                                    dir="rtl"
                                                                    className="form-control rounded-3 border-light border-2 shadow-none text-end pe-3"
                                                                    value={siteInfo?.address_ar || ''}
                                                                    onChange={(e) => { setHasChanges(true); setSiteInfo({ ...siteInfo, address_ar: e.target.value }) }}
                                                                />
                                                            </div>
                                                            <div className="col-12 col-sm-6">
                                                                <label className="small fw-bold text-muted mb-1 text-uppercase" style={{ fontSize: '10px' }}>{t("Phone Number")}</label>
                                                                <input
                                                                    className="form-control rounded-3 border-light border-2 shadow-none ps-3"
                                                                    // placeholder="+974..."
                                                                    value={siteInfo?.phone || ''}
                                                                    onChange={(e) => { setHasChanges(true); setSiteInfo({ ...siteInfo, phone: e.target.value }) }}
                                                                />
                                                            </div>
                                                            <div className="col-12 col-sm-6">
                                                                <label className="small fw-bold text-muted mb-1 text-uppercase" style={{ fontSize: '10px' }}>{t("Currency")}</label>
                                                                <select
                                                                    className="form-select rounded-3 border-light border-2 shadow-none ps-3"
                                                                    value={currency || ''}
                                                                    onChange={(e) => { setHasChanges(true); updateState({ currency: e.target.value }); setCurrency(e.target.value); }}
                                                                >
                                                                    <option value="">Select Currency</option>
                                                                    {CURRENCIES.map(c => (
                                                                        <option key={c.code} value={c.code}>
                                                                            {c.code} ({c.symbol}) - {c.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* 2. Menu Sections Title */}
                            <div className="d-flex align-items-center gap-3 mb-4">
                                <hr className="flex-grow-1 opacity-10" />
                                <h5 className="mb-0 fw-bold text-muted text-uppercase small" style={{ letterSpacing: '2px' }}>{t("Menu Sections")}</h5>
                                <hr className="flex-grow-1 opacity-10" />
                            </div>

                            {/* Sections Wrapper */}
                            <div className="d-flex flex-column gap-3 mb-5">
                                {sections.map((section) => (
                                    <div
                                        key={section.id}
                                        className={`card border-0 shadow-sm rounded-4 overflow-hidden transition-all ${activeSection === section.id ? 'ring-2' : ''}`}
                                        style={activeSection === section.id ? { ringColor: THEME_COLOR, ringWidth: '2px' } : {}}
                                    >
                                        {/* Section Header */}
                                        <div
                                            className="card-header bg-white border-0 p-3 d-flex align-items-center justify-content-between cursor-pointer"
                                            onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                                        >
                                            <div className="d-flex align-items-center gap-3 min-w-0">
                                                <div
                                                    className="rounded-3 bg-light d-flex align-items-center justify-content-center overflow-hidden border"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    {section.imagePreview ? (
                                                        <img src={section.imagePreview} className="w-100 h-100 object-fit-cover" alt="" />
                                                    ) : (
                                                        <ImageIcon size={18} className="text-muted opacity-50" />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <h6 className="mb-0 fw-bold text-dark text-truncate">{section.name}</h6>
                                                        {section.name_ar && <span className="text-muted small d-none d-sm-inline">• {section.name_ar}</span>}
                                                    </div>
                                                    <span className="text-muted x-small d-block mt-0.5">
                                                        {section.items?.length || 0} items
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="d-flex align-items-center gap-1">
                                                    <button
                                                        className="btn btn-link btn-sm p-1 text-muted hover-text-danger transition-colors"
                                                        onClick={(e) => { deleteSection(section.id, e); }}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                                <div className={`transition-transform duration-300 ${activeSection === section.id ? 'rotate-180' : ''}`} style={{ transform: activeSection === section.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                    <ChevronDown size={20} className="text-muted" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Section Content */}
                                        <AnimatePresence>
                                            {activeSection === section.id && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                >
                                                    <div className="card-body p-3 pt-0 border-top bg-light bg-opacity-25">
                                                        {/* Section Config Area */}
                                                        <div className="bg-white p-3 rounded-4 border-light border mt-3 mb-4 shadow-sm">
                                                            <div className="d-flex align-items-center gap-2 mb-3">
                                                                <div className="p-1 rounded bg-primary bg-opacity-10 text-primary">
                                                                    <Edit2 size={14} />
                                                                </div>
                                                                <span className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Section Settings</span>
                                                            </div>

                                                            <div className="row g-3">
                                                                {/* Image Upload */}
                                                                <div className="col-12 col-md-auto">
                                                                    <div className="position-relative mx-auto mx-md-0" style={{ width: '100px' }}>
                                                                        <div
                                                                            className="rounded-4 bg-light border-dashed border-2 d-flex flex-column align-items-center justify-content-center cursor-pointer overflow-hidden transition-all hover-bg-silver"
                                                                            style={{ height: '100px', width: '100px', borderStyle: 'dashed' }}
                                                                            onClick={() => document.getElementById(`section-img-${section.id}`).click()}
                                                                        >
                                                                            {section.imagePreview ? (
                                                                                <img src={section.imagePreview} className="w-100 h-100 object-fit-cover" alt="" />
                                                                            ) : (
                                                                                <>
                                                                                    <Camera size={24} className="text-muted mb-1 opacity-50" />
                                                                                    <span style={{ fontSize: '8px' }} className="fw-bold text-muted">UPLOAD</span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        <input
                                                                            type="file"
                                                                            id={`section-img-${section.id}`}
                                                                            className="d-none"
                                                                            accept="image/*"
                                                                            onChange={(e) => handleSectionImage(section.id, e)}
                                                                        />
                                                                        {section.imagePreview && (
                                                                            <button
                                                                                className="btn btn-danger btn-sm rounded-circle p-1 position-absolute top-0 end-0 m-1 shadow-sm border-0"
                                                                                onClick={(e) => { e.stopPropagation(); updateSection(section.id, { imagePreview: null, imageFile: null }); }}
                                                                                style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                            >
                                                                                <X size={12} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Text Fields */}
                                                                <div className="col">
                                                                    <div className="row g-2">
                                                                        <div className="col-12 col-sm-6">
                                                                            <label className="x-small fw-bold text-muted mb-1 d-block" style={{ fontSize: '9px' }}>NAME (EN)</label>
                                                                            <input
                                                                                className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none"
                                                                                value={section.name}
                                                                                onChange={(e) => { setHasChanges(true); updateSection(section.id, { name: e.target.value }) }}
                                                                            />
                                                                        </div>
                                                                        <div className="col-12 col-sm-6">
                                                                            <label className="x-small fw-bold text-muted mb-1 d-block text-end" style={{ fontSize: '9px' }}>Name (AR)</label>
                                                                            <input
                                                                                dir="rtl"
                                                                                className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none text-end"
                                                                                value={section.name_ar || ''}
                                                                                onChange={(e) => { setHasChanges(true); updateSection(section.id, { name_ar: e.target.value }) }}
                                                                            />
                                                                        </div>
                                                                        <div className="col-12 col-sm-6">
                                                                            <label className="x-small fw-bold text-muted mb-1 d-block" style={{ fontSize: '9px' }}>DESC (EN)</label>
                                                                            <input
                                                                                className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none"
                                                                                placeholder="Optional description"
                                                                                value={section.description || ''}
                                                                                onChange={(e) => { setHasChanges(true); updateSection(section.id, { description: e.target.value }) }}
                                                                            />
                                                                        </div>
                                                                        <div className="col-12 col-sm-6">
                                                                            <label className="x-small fw-bold text-muted mb-1 d-block text-end" style={{ fontSize: '9px' }}>DESC (AR)</label>
                                                                            <input
                                                                                dir="rtl"
                                                                                className="form-control form-control-sm rounded-3 border-secondary border-opacity-25 shadow-none text-end"
                                                                                placeholder="وصف اختياري"
                                                                                value={section.description_ar || ''}
                                                                                onChange={(e) => { setHasChanges(true); updateSection(section.id, { description_ar: e.target.value }) }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Items Header */}
                                                        <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="p-1 rounded bg-secondary bg-opacity-10 text-secondary">
                                                                    <Utensils size={14} />
                                                                </div>
                                                                <span className="small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                                    Section Items ({section.items?.length || 0})
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Item List */}
                                                        <div className="d-flex flex-column gap-2 mb-2">
                                                            {(section.items || []).map((item) => (
                                                                <div key={item.id} className="card border-light bg-white rounded-4 overflow-hidden shadow-none transition-all hover-shadow-sm group-item">
                                                                    <div className="p-2 px-3">
                                                                        <div className="d-flex gap-3 align-items-center">
                                                                            <div className="bg-light rounded-3 overflow-hidden flex-shrink-0 border shadow-xs" style={{ width: '50px', height: '50px' }}>
                                                                                {item.imagePreview ? (
                                                                                    <img src={item.imagePreview} alt={item.name} className="w-100 h-100 object-fit-cover" />
                                                                                ) : (
                                                                                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-30 bg-white">
                                                                                        <Utensils size={12} />
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            <div className="flex-grow-1 min-w-0">
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <h6 className="mb-0 text-truncate fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{item.name}</h6>
                                                                                    {item.is_promo && <span className="bg-danger rounded-circle" style={{ width: '6px', height: '6px' }} title="On Promo"></span>}
                                                                                </div>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <span className="fw-bold text-primary" style={{ fontSize: '0.8rem' }}>
                                                                                        {item.is_promo && item.promo_price
                                                                                            ? `${currency}${item.promo_price}`
                                                                                            : (item.price ? `${currency}${item.price}` : 'Free')}
                                                                                    </span>
                                                                                </div>
                                                                            </div>

                                                                            <div className="d-flex gap-1">
                                                                                <button
                                                                                    onClick={() => handleEditItem(item)}
                                                                                    className="btn btn-sm p-1 rounded-circle hover-bg-light text-muted"
                                                                                >
                                                                                    <Edit2 size={13} />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => deleteItem(item.id)}
                                                                                    className="btn btn-sm p-1 rounded-circle hover-bg-light text-danger"
                                                                                >
                                                                                    <Trash2 size={13} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}

                                                            {/* Add Item Card Button */}
                                                            <motion.div
                                                                whileHover={{ scale: 1.01 }}
                                                                whileTap={{ scale: 0.99 }}
                                                                onClick={() => {
                                                                    if (itemLimit?.isReached) {
                                                                        setShowUpgradeModal(true);
                                                                        return;
                                                                    }
                                                                    setActiveSection(section.id);
                                                                    setEditingItem(null);
                                                                    setIsDialogOpen(true);
                                                                }}
                                                                className="card border-2 border-dashed bg-white rounded-4 overflow-hidden shadow-sm transition-all cursor-pointer add-item-card"
                                                                style={{
                                                                    borderColor: THEME_COLOR,
                                                                    borderStyle: 'dashed'
                                                                }}
                                                            >
                                                                <div className="card-body p-3 d-flex align-items-center justify-content-center gap-2">
                                                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', backgroundColor: `${THEME_COLOR}15` }}>
                                                                        <Plus size={18} style={{ color: THEME_COLOR }} />
                                                                    </div>
                                                                    <span className="fw-bold" style={{ color: THEME_COLOR, fontSize: '0.9rem' }}>Add New Item</span>
                                                                </div>
                                                            </motion.div>

                                                            {(!section.items || section.items.length === 0) && (
                                                                <div className="text-center py-4 border rounded-4 bg-white bg-opacity-50 border-dashed" style={{ borderStyle: 'dashed' }}>
                                                                    <p className="text-muted mb-0 x-small fw-medium opacity-50">No items in this section</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}

                                {/* Add New Section Card Button */}
                                <motion.div
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                    onClick={addSection}
                                    className="card border-2 border-dashed bg-white rounded-4 overflow-hidden shadow-sm transition-all cursor-pointer add-section-card"
                                    style={{ borderColor: THEME_COLOR, borderStyle: 'dashed' }}
                                >
                                    <div className="card-body p-4 d-flex align-items-center justify-content-center gap-3">
                                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: `${THEME_COLOR}20` }}>
                                            <Plus size={24} style={{ color: THEME_COLOR, strokeWidth: 2.5 }} />
                                        </div>
                                        <div className="text-center">
                                            <h6 className="mb-0 fw-bold" style={{ color: THEME_COLOR }}>
                                                {t?.("Add New Section") || 'Add New Section'}
                                            </h6>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: Live Preview */}
                <div className="d-none d-xl-flex bg-white p-1 border-start shadow-lg" style={{ width: '450px', zIndex: 5, flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="d-flex align-items-center justify-content-between m-2 flex-shrink-0">
                        <div className="d-flex align-items-center gap-2">
                            <div className="bg-success rounded-circle" style={{ width: '8px', height: '8px' }}></div>
                            <h6 className="text-muted small text-uppercase fw-bold mb-0" style={{ letterSpacing: '1px' }}>Live Menu Preview</h6>
                        </div>
                        <span className="badge bg-light text-muted fw-normal px-2 py-1" style={{ fontSize: '10px' }}>REAL-TIME UPDATES</span>
                    </div>
                    <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                        <div className="border border-4 rounded-5 p-2 bg-dark shadow-lg position-relative" style={{ width: '320px', height: '100%', maxHeight: '640px', borderColor: '#333 !important' }}>
                            <div className="w-100 h-100 rounded-4 bg-white overflow-hidden shadow-inner">
                                <MobileView state={{ ...localState, siteInfo }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>



            {/* Change Tracking Exit Confirmation Modal */}
            < Modal isOpen={showExitConfirm} toggle={() => setShowExitConfirm(false)} centered size="sm" >
                <div className="p-4 text-center">
                    <div className="mx-auto bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                        <AlertTriangle className="text-warning" size={28} />
                    </div>
                    <h5 className="fw-bold mb-2">{t("Unsaved Changes")}</h5>
                    <p className="text-muted small mb-4">
                        {t("You have unsaved changes. Are you sure you want to discard them and exit?")}
                    </p>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button color="light" className="border px-4" onClick={() => setShowExitConfirm(false)}>{t("Cancel")}</Button>
                        <Button color="danger" className="px-4" onClick={() => onClose(false)}>{t("Exit without Saving")}</Button>
                    </div>
                </div>
            </Modal >

            <PlanUpgradeModal
                isOpen={showUpgradeModal}
                toggle={() => setShowUpgradeModal(!showUpgradeModal)}
            />

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .hover-shadow:hover { box-shadow: 0 .5rem 1rem rgba(0,0,0,.08)!important; }
                .hover-shadow-sm:hover { box-shadow: 0 .125rem .25rem rgba(0,0,0,.075)!important; }
                .hover-bg-silver:hover { background-color: #f8f9fa!important; }
                .hover-bg-light:hover { background-color: #f1f3f5!important; }
                .hover-text-primary:hover { color: ${THEME_COLOR}!important; }
                .hover-text-danger:hover { color: #dc3545!important; }
                .hover-border-silver:hover { border-color: #dee2e6!important; }
                .transition-all { transition: all 0.2s ease; }
                .transition-colors { transition: color 0.2s ease, background-color 0.2s ease; }
                .max-w-4xl { max-width: 56rem; }
                .ring-2 { box-shadow: 0 0 0 2px ${THEME_COLOR}; }
                .x-small { font-size: 0.7rem; }
                .btn-xs { padding: 0.1rem 0.4rem; font-size: 0.75rem; }
                .group-item:hover { transform: translateY(-1px); }
                .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }

                @keyframes pulse-border {
                    0%, 100% {
                        border-color: ${THEME_COLOR};
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1), 0 0 0 0 ${THEME_COLOR}40;
                    }
                    50% {
                        border-color: ${THEME_COLOR}dd;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15), 0 0 0 6px ${THEME_COLOR}15;
                    }
                }

                .add-section-card, .add-item-card {
                    animation: pulse-border 2.5s ease-in-out infinite;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .add-section-card:hover, .add-item-card:hover {
                    animation: none;
                    border-color: ${THEME_COLOR} !important;
                    box-shadow: 0 6px 16px ${THEME_COLOR}40 !important;
                    transform: translateY(-2px);
                }

                *::-webkit-scrollbar {
                  width: 5px;
                }
                *::-webkit-scrollbar-track {
                  background: transparent;
                }
                *::-webkit-scrollbar-thumb {
                  background: #ddd;
                  border-radius: 10px;
                }
                *::-webkit-scrollbar-thumb:hover {
                  background: #ccc;
                }
            `}</style>

            <AddItemDialog
                isOpen={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                    setEditingItem(null);
                }}
                onAdd={addItem}
                itemToEdit={editingItem}
                businessType={'restaurant'}
                currency={currency}
            />
        </div>
    );
};

export default EditBranchMenu;
